import React, { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronLeft, ChevronRight, MapPin, History } from 'lucide-react';
import { useGiwahs } from '../context/GiwahsContext';
import { useSuppliers } from '../data/hooks';
import Img from './Img';
import { track, EVENTS } from '../lib/analytics';

/**
 * <RecentlyViewedStrip />
 *
 * Subtle horizontal-scroll strip surfacing the buyer's last-viewed
 * factories. Client-side only — reads `recentlyViewed` (legacy_id list)
 * from GiwahsContext and resolves each id against the suppliers list.
 *
 * If the list is empty we render a quiet, design-consistent empty state
 * (when `showEmptyState` prop is true, defaults true) — or render nothing.
 *
 * `placement` is recorded in the analytics payload for funnel reporting.
 */
const RecentlyViewedStrip = ({ placement = 'home', showEmptyState = true, className = '' }) => {
  const { recentlyViewed } = useGiwahs();
  const { data: suppliers } = useSuppliers();
  const scrollerRef = useRef(null);

  // Resolve legacy_ids → supplier objects, preserving recently-viewed order.
  const items = useMemo(() => {
    if (!suppliers?.length || !recentlyViewed?.length) return [];
    const bySlug = new Map();
    suppliers.forEach(s => bySlug.set(s.id, s));
    return recentlyViewed
      .map(id => bySlug.get(id))
      .filter(Boolean)
      .slice(0, 12);
  }, [recentlyViewed, suppliers]);

  if (items.length === 0) {
    if (!showEmptyState) return null;
    return (
      <section data-testid="recently-viewed-empty" className={`border-b border-black bg-[#F5F5F0] ${className}`}>
        <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8 md:py-14">
          <span className="label-overline text-neutral-500">/ Recently viewed</span>
          <div className="mt-3 flex items-start gap-3">
            <History size={14} strokeWidth={1.5} className="mt-1 text-neutral-500" />
            <p className="max-w-md text-sm text-neutral-600">
              Factories you view appear here for quick return — keep browsing the directory to populate this strip.
            </p>
          </div>
        </div>
      </section>
    );
  }

  const scroll = (delta) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <section data-testid="recently-viewed-strip" className={`border-b border-black bg-white ${className}`}>
      <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8 md:py-14">
        <div className="flex items-end justify-between gap-4">
          <div>
            <span className="label-overline">/ Recently viewed</span>
            <h2 className="mt-2 font-display tracking-tight" style={{ fontWeight: 700, fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', lineHeight: 1 }}>
              Pick up where you left off.
            </h2>
          </div>
          <div className="hidden gap-2 md:flex">
            <button
              data-testid="recently-viewed-scroll-left"
              onClick={() => scroll(-340)}
              aria-label="Scroll recently viewed left"
              className="flex h-9 w-9 items-center justify-center border border-black hover:bg-black hover:text-white"
            >
              <ChevronLeft size={14} strokeWidth={1.5} />
            </button>
            <button
              data-testid="recently-viewed-scroll-right"
              onClick={() => scroll(340)}
              aria-label="Scroll recently viewed right"
              className="flex h-9 w-9 items-center justify-center border border-black hover:bg-black hover:text-white"
            >
              <ChevronRight size={14} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="mt-6 -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 scroll-pl-5 md:mx-0 md:px-0 md:scroll-pl-0"
          style={{ scrollbarWidth: 'thin' }}
        >
          {items.map((s) => (
            <Link
              key={s.id}
              to={`/supplier/${s.slug}`}
              data-testid={`recently-viewed-card-${s.id}`}
              onClick={() => track(EVENTS.RECENTLY_VIEWED_CLICKED, { supplier_id: s.id, placement, position: items.indexOf(s) })}
              className="group relative flex w-[280px] shrink-0 snap-start flex-col border border-black bg-white transition-transform hover:-translate-y-0.5"
            >
              <div className="relative aspect-[4/3] overflow-hidden border-b border-black bg-neutral-100">
                <Img
                  src={s.heroImage}
                  alt={s.name}
                  variant="card"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col gap-2 p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="label-overline text-neutral-500">{s.type}</span>
                  <span className="rounded-full border border-black bg-white px-2.5 py-0.5 font-mono-data text-[10px]">{s.trustScore}/100</span>
                </div>
                <h3 className="font-display text-lg leading-tight tracking-tight">{s.name}</h3>
                <div className="flex items-center gap-1.5 text-xs text-neutral-600">
                  <MapPin size={12} strokeWidth={1.5} />
                  <span>{s.country}</span>
                </div>
                <div className="mt-auto flex items-center justify-between border-t border-neutral-200 pt-3">
                  <span className="label-overline">View factory</span>
                  <ArrowUpRight size={14} strokeWidth={1.5} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RecentlyViewedStrip;
